// The KSF surf map archive (public Google Drive folders linked from github.com/OuiSURF/Surf_Maps).
// public/maps/catalog.json is generated by scripts/build-catalog.mjs.

export interface CatalogEntry {
  /** Map name without extension, e.g. "surf_utopia_njv". */
  name: string;
  /** Google Drive file id of the archive. */
  driveId: string;
  /** Archive extension in the Drive folder ("rar", "zip", "bz2", "bsp"). */
  archive?: string;
  /** KSF tier (1 = easiest .. 8), null if unknown. */
  tier: number | null;
  type: 'linear' | 'staged' | 'staged-linear' | null;
  /** SurfTimer zone presets exist for this map (exact name or a matching build). */
  hasZones: boolean;
  /** A classic (the Classics tab): from public/maps/popularity.json when present. */
  featured: boolean;
  /**
   * How popular the map was on CS:GO surf servers (about 2015-2020), 0..100, from public/maps/popularity.json
   * (KSF play statistics, Steam Workshop subscribers, server and community data); absent when unknown.
   */
  popularity?: number;
}

interface CatalogFile {
  generated: string;
  source: string;
  maps: CatalogEntry[];
}

let entries: CatalogEntry[] | null = null;
let byName = new Map<string, CatalogEntry>();
let loading: Promise<CatalogEntry[]> | null = null;

/** URL of a file in public/ that works with any Vite base path. */
export function publicUrl(path: string): string {
  const base = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || './';
  return base.endsWith('/') ? base + path : `${base}/${path}`;
}

export async function loadCatalog(): Promise<CatalogEntry[]> {
  if (entries) return entries;
  if (!loading) {
    loading = (async () => {
      const res = await fetch(publicUrl('maps/catalog.json'));
      if (!res.ok) throw new Error(`catalog: HTTP ${res.status}`);
      const file = (await res.json()) as CatalogFile;
      setCatalog(file.maps);
      return file.maps;
    })();
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
}

/** Installs catalog entries directly (tests, or an already-fetched catalog). */
export function setCatalog(maps: CatalogEntry[]): void {
  entries = maps;
  byName = new Map(maps.map((m) => [m.name.toLowerCase(), m]));
}

export function getCatalogEntry(name: string): CatalogEntry | undefined {
  return byName.get(name.toLowerCase().replace(/\.bsp$/, ''));
}

/** KSF-style tier colors. */
export function tierColor(tier: number | null): string {
  switch (tier) {
    case 1:
      return '#3fd16b';
    case 2:
      return '#9be34b';
    case 3:
      return '#e6d23f';
    case 4:
      return '#f0a03c';
    case 5:
      return '#ef6b3a';
    case 6:
      return '#e5394a';
    case 7:
      return '#c23ad8';
    case 8:
      return '#8a4dff';
    default:
      return '#8a96a3';
  }
}
